import moment from 'moment';

/**
 * The hourly vocabulary this report runs on.
 *
 * Every other GSC tool here reasons in calendar dates. This one cannot: Search Console's hourly
 * rows are keyed by a full RFC 3339 timestamp with a Pacific offset, and the whole point of the
 * report is the boundary between the hours that have finished collecting and the hours that have
 * not. So hours are kept as instants, never as an hour-of-day — that is also what keeps the two
 * repeated 01:00s of a DST fall-back as the two different hours they are.
 */

/** The timezone Search Console reports these hours in. The offsets come back inside the keys. */
export const REPORTING_TIMEZONE = 'America/Los_Angeles';

/** Search Console's hourly data state, and the dimension that returns one row per hour. */
export const HOURLY_DATA_STATE = 'hourly_all';
export const HOUR_DIMENSION = 'hour';

/** Web Search is the only surface Search Console reports hourly data for. */
export const HOURLY_SEARCH_TYPE = 'web';

/** How many hours the report covers, counting back from the latest hour that was returned. */
export const WINDOW_HOURS = 24;

/** How far back the comparison looks: the same hours one week earlier. */
export const COMPARISON_DAYS = 7;

/**
 * How many days of hourly history Search Console keeps. The comparison reaches back a week, which
 * is inside it but not by much, so a property with a gap can legitimately have no counterpart
 * hours — the report says that rather than drawing a comparison against nothing.
 */
export const HOURLY_RETENTION_DAYS = 10;

/**
 * How many trailing hours this report treats as still collecting.
 *
 * This is a GDDash assumption and is labelled as one everywhere it has an effect. Search Console
 * answers the question properly, in `metadata.first_incomplete_hour`, but the Cloud Function this
 * app reads through returns the response's rows only — the metadata never reaches the browser.
 * Without it, no row can be proven complete: a low hour looks exactly the same whether it was
 * quiet or is still filling.
 *
 * So the boundary is drawn conservatively and stated: the most recent hours are called
 * provisional, they are kept out of the comparable totals by default, and the coverage block says
 * the boundary was assumed rather than supplied. Wiring the metadata through the Cloud Function
 * would replace this constant with Google's own answer and nothing else would need to change.
 */
export const ASSUMED_PROVISIONAL_HOURS = 2;

export const COMPARISON = { WEEK_AGO: 'week_ago', NONE: 'none' };

/** Whether the trailing provisional hours take part in the chart and the totals. */
export const COVERAGE = { COMPARABLE: 'comparable', ALL: 'all' };

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Today's calendar date in Pacific time, which is the calendar the request's date bounds are in.
 *
 * Read from Intl rather than from moment: the app does not bundle moment-timezone, and the
 * browser's own zone is not the reporting zone — a user in Tokyo asking for "today" would
 * otherwise bound the request a day ahead of the data.
 */
export function todayInReportingZone(now = new Date()) {
    // en-CA formats as YYYY-MM-DD, which is the format the API wants.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: REPORTING_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}

/**
 * The date bounds one request covers.
 *
 * Two Pacific calendar days, because a 24-hour window ending at any hour of today reaches back
 * into yesterday and never further. The exact hours are cut from the response afterwards: the API
 * takes dates, and the window this report wants is a span of hours that does not line up with
 * either end of them.
 */
export function requestDates(today, daysBack = 0) {
    const end = moment(today, DATE_FORMAT).subtract(daysBack, 'days');
    return {
        startDate: end.clone().subtract(1, 'days').format(DATE_FORMAT),
        endDate: end.format(DATE_FORMAT),
    };
}

/**
 * One returned hourly row, read as an instant.
 *
 * `moment.parseZone` keeps the offset Search Console sent rather than converting to the browser's
 * zone, so the hour prints as the Pacific hour it is — including the offset itself, which is the
 * only thing distinguishing the two 01:00s on the day the clocks go back.
 */
export function parseHour(key) {
    if (!key) return null;
    const parsed = moment.parseZone(key);
    return parsed.isValid() ? parsed : null;
}

/** The hour as the chart labels it: the Pacific clock time, with the date when it changes. */
export function hourLabel(hour, { withDate = false } = {}) {
    return hour.format(withDate ? 'DD MMM HH:mm' : 'HH:mm');
}

/**
 * The returned rows as hours, in chronological order, with anything unparseable dropped.
 *
 * Duplicate timestamps are collapsed to the last one seen rather than added together: a revision
 * of an hour replaces that hour, and summing them would silently double it. That is the "late
 * revisions replace matching timestamp rows, never append duplicates" rule, enforced where the
 * rows are read rather than trusted to arrive clean.
 */
export function readHours(rows) {
    const byInstant = new Map();

    for (const row of rows ?? []) {
        const hour = parseHour(row?.keys?.[0]);
        if (!hour) continue;
        byInstant.set(hour.valueOf(), { instant: hour.valueOf(), hour, row });
    }

    return [...byInstant.values()].sort((a, b) => a.instant - b.instant);
}

/**
 * The window the report is about: the `WINDOW_HOURS` hours ending at the latest hour returned.
 *
 * Derived from the response rather than from the clock. The latest hour Search Console has is not
 * the current hour — there is a reporting delay, and how long it is is not something the browser
 * can know — so a window anchored on "now" would open with empty hours at its newest end and read
 * as a traffic collapse.
 */
export function resolveWindow(hours) {
    if (!hours.length) return null;

    const latest = hours[hours.length - 1].hour;
    const start = latest.clone().subtract(WINDOW_HOURS - 1, 'hours');

    return { start, end: latest, startInstant: start.valueOf(), endInstant: latest.valueOf() };
}

/** The same window one week earlier, as instants, for matching the baseline rows against. */
export function shiftWindow(window) {
    if (!window) return null;

    const start = window.start.clone().subtract(COMPARISON_DAYS, 'days');
    const end = window.end.clone().subtract(COMPARISON_DAYS, 'days');

    return { start, end, startInstant: start.valueOf(), endInstant: end.valueOf() };
}

/** The hours of a response that fall inside a window, which is what the date bounds overshot. */
export function hoursInWindow(hours, window) {
    if (!window) return [];
    return hours.filter(
        (item) => item.instant >= window.startInstant && item.instant <= window.endInstant,
    );
}

/**
 * The instant the assumed provisional stretch begins: the first of the trailing hours this report
 * will not call complete.
 *
 * Counted back from the latest hour returned rather than from the window's end, because they are
 * the same hour and it is the newest *data* that is provisional, not the newest slot.
 */
export function provisionalFrom(window) {
    if (!window) return null;
    return window.end
        .clone()
        .subtract(ASSUMED_PROVISIONAL_HOURS - 1, 'hours')
        .valueOf();
}

/**
 * The report's hours, each carrying what is known about it and what it has to compare against.
 *
 * Three facts per hour, kept apart because they fail differently: whether the hour was returned
 * at all, whether it is old enough to be treated as finished, and whether the hour a week earlier
 * came back too. Only an hour that is all three takes part in a comparison — comparing a
 * still-filling hour against a settled one is the mistake this whole report exists to prevent.
 *
 * Every hour of the window gets an entry, including the ones Search Console did not return, so a
 * gap stays a gap instead of closing up and making the window look continuous.
 *
 * Hours are matched on the instant and *displayed* from the row that came back. The two differ
 * across a daylight-saving change: the cursor walking the window carries the offset the window's
 * newest hour was parsed with, so an hour from the other side of the transition would be printed
 * an hour out even though it was paired correctly. Taking the label from the returned row keeps
 * every hour that exists showing the Pacific clock time Search Console actually gave it.
 */
export function buildHours({ window, currentHours, baselineHours }) {
    if (!window) return [];

    const current = new Map(currentHours.map((item) => [item.instant, item]));
    const baseline = new Map(baselineHours.map((item) => [item.instant, item]));
    const provisionalStart = provisionalFrom(window);

    const rows = [];
    const cursor = window.start.clone();

    while (cursor.valueOf() <= window.endInstant) {
        const instant = cursor.valueOf();
        const priorCursor = cursor.clone().subtract(COMPARISON_DAYS, 'days');

        const currentItem = current.get(instant) ?? null;
        const priorItem = baseline.get(priorCursor.valueOf()) ?? null;
        const provisional = instant >= provisionalStart;

        // The returned row's own timestamp where there is one; the walked cursor only stands in
        // for hours that have nothing to speak for them.
        const hour = currentItem ? currentItem.hour : cursor.clone();
        const priorHour = priorItem ? priorItem.hour : priorCursor;

        rows.push({
            id: hour.format(),
            instant,
            hour,
            priorHour,
            key: hour.format(),
            priorKey: priorHour.format(),
            returned: !!currentItem,
            priorReturned: !!priorItem,
            provisional,
            // The one flag everything downstream reads: an hour that may be totalled, charted and
            // compared without qualification.
            comparable: !!currentItem && !!priorItem && !provisional,
            currentRow: currentItem?.row ?? null,
            priorRow: priorItem?.row ?? null,
        });

        cursor.add(1, 'hours');
    }

    return rows;
}

/**
 * Which hours a mode draws and totals.
 *
 * Comparable mode is the honest default: only hours that are settled on both sides. Including the
 * provisional ones is offered because a reader watching for a sudden drop wants to see the newest
 * hours even knowing they will be revised — but they are marked, they are never the basis of the
 * comparison, and the block says values may change.
 */
export function selectedHours(rows, coverage) {
    if (coverage === COVERAGE.ALL) return rows.filter((row) => row.returned);
    return rows.filter((row) => row.comparable);
}

/** "18 Oct 16:00 → 19 Oct 15:00 PT", the way every block names a window. */
export function formatWindow(window) {
    if (!window) return '—';
    return `${hourLabel(window.start, { withDate: true })} → ${hourLabel(window.end, {
        withDate: true,
    })} (UTC${window.end.format('Z')})`;
}
